import {useEffect, useId, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {X} from "lucide-react";
import "../finance/financeOperations.css";

const focusableElements = (container) =>
    Array.from(
        container.querySelectorAll(
            "button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex='-1'])",
        ),
    );

const EMPTY_FIELDS = [];

const initialValues = (fields) =>
    fields.reduce((values, field) => ({...values, [field.name]: field.value || ""}), {});

export const FinancialCommandDialog = ({
    open,
    title,
    description,
    entity,
    details = [],
    fields = EMPTY_FIELDS,
    dialogKey,
    confirmLabel = "Подтвердить",
    pending = false,
    error = null,
    fallbackFocusRef = null,
    onCancel,
    onConfirm,
    onFieldChange,
}) => {
    const titleId = useId();
    const descriptionId = useId();
    const validationId = useId();
    const dialogRef = useRef(null);
    const firstFieldRef = useRef(null);
    const submitRef = useRef(null);
    const invokingElementRef = useRef(null);
    const activeDialogIdentityRef = useRef(null);
    const [values, setValues] = useState(() => initialValues(fields));
    const [validationError, setValidationError] = useState("");
    const dialogIdentity = dialogKey ?? `${title || ""}:${entity || ""}`;

    useEffect(() => {
        if (!open) return undefined;
        invokingElementRef.current = document.activeElement;
        const fallbackFocusElement = fallbackFocusRef?.current;
        const frame = window.requestAnimationFrame(() =>
            (firstFieldRef.current || submitRef.current)?.focus(),
        );
        return () => {
            window.cancelAnimationFrame(frame);
            const invokingElement = invokingElementRef.current;
            const focusTarget =
                invokingElement && document.contains(invokingElement)
                    ? invokingElement
                    : fallbackFocusElement;
            if (focusTarget && document.contains(focusTarget)) focusTarget.focus();
            invokingElementRef.current = null;
        };
    }, [fallbackFocusRef, open]);

    useEffect(() => {
        if (!open) {
            activeDialogIdentityRef.current = null;
            return;
        }
        if (activeDialogIdentityRef.current === dialogIdentity) return;
        activeDialogIdentityRef.current = dialogIdentity;
        setValues(initialValues(fields));
        setValidationError("");
    }, [dialogIdentity, fields, open]);

    useEffect(() => {
        if (!open) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                if (!pending) onCancel();
                return;
            }
            if (event.key !== "Tab" || !dialogRef.current) return;
            const focusable = focusableElements(dialogRef.current);
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [onCancel, open, pending]);

    if (!open) return null;

    const submit = (event) => {
        event.preventDefault();
        const normalized = Object.fromEntries(
            fields.map((field) => [field.name, String(values[field.name] || "").trim()]),
        );
        if (fields.some((field) => field.required && !normalized[field.name])) {
            setValidationError("Заполните обязательные поля");
            return;
        }
        if (!pending) onConfirm(normalized);
    };

    const alertMessage = validationError || error?.message;
    return createPortal(
        <div
            className="finance-dialog-backdrop"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget && !pending) onCancel();
            }}
        >
            <section
                ref={dialogRef}
                className="finance-dialog"
                role="dialog"
                aria-modal="true"
                aria-busy={pending}
                aria-labelledby={titleId}
                aria-describedby={description ? descriptionId : undefined}
            >
                <form onSubmit={submit}>
                    <header className="finance-dialog-header">
                        <div>
                            <p className="finance-dialog-entity">{entity}</p>
                            <h2 id={titleId}>{title}</h2>
                        </div>
                        <button
                            className="finance-icon-button"
                            type="button"
                            aria-label="Закрыть"
                            title="Закрыть"
                            disabled={pending}
                            onClick={onCancel}
                        >
                            <X aria-hidden="true" size={18} />
                        </button>
                    </header>
                    {description ? <p id={descriptionId}>{description}</p> : null}
                    {details.length ? (
                        <dl className="finance-dialog-details">
                            {details.map((detail) => (
                                <div key={detail.label}>
                                    <dt>{detail.label}</dt>
                                    <dd>{detail.value}</dd>
                                </div>
                            ))}
                        </dl>
                    ) : null}
                    <div className="finance-dialog-fields">
                        {fields.map((field, index) => {
                            const commonProps = {
                                ref: index === 0 ? firstFieldRef : undefined,
                                name: field.name,
                                value: values[field.name] || "",
                                disabled: pending || field.disabled,
                                required: field.required,
                                "aria-invalid": Boolean(alertMessage),
                                onChange: (event) => {
                                    const nextValue = event.target.value;
                                    setValues((current) => {
                                        const next = {...current, [field.name]: nextValue};
                                        for (const cleared of field.clears || []) {
                                            next[cleared] = "";
                                        }
                                        return next;
                                    });
                                    onFieldChange?.(field.name, nextValue);
                                    setValidationError("");
                                },
                            };
                            return (
                                <label key={field.name}>
                                    <span>{field.label}</span>
                                    {field.type === "select" ? (
                                        <select {...commonProps}>
                                            {(field.options || []).map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        (() => {
                                            const Input = field.multiline ? "textarea" : "input";
                                            return (
                                                <Input
                                                    {...commonProps}
                                                    type={field.multiline ? undefined : field.type || "text"}
                                                />
                                            );
                                        })()
                                    )}
                                </label>
                            );
                        })}
                    </div>
                    {alertMessage ? (
                        <div id={validationId} className="finance-dialog-error" role="alert">
                            <p>{alertMessage}</p>
                            {Object.values(error?.fieldErrors || {}).flat().map((message) => (
                                <p key={message}>{message}</p>
                            ))}
                        </div>
                    ) : null}
                    <footer className="finance-dialog-footer">
                        <button type="button" className="admin-button admin-button-secondary" disabled={pending} onClick={onCancel}>
                            Отмена
                        </button>
                        <button ref={submitRef} type="submit" className="admin-button admin-button-primary" disabled={pending}>
                            {pending ? "Подтверждение выполняется" : confirmLabel}
                        </button>
                    </footer>
                </form>
            </section>
        </div>,
        document.body,
    );
};
